import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useDraft } from '../../state/draft';
import { STARTER_PROMPTS } from '@/webapp/plan/scaffolds';
import { Button, Screen } from '../../ui';

/** Long enough for any real sentence; short enough that a link can't paste in an essay. */
const MAX_LINKED_IDEA = 1000;

/**
 * The front door, ported from src/app/(onboarding)/create.tsx. One idea in, nothing else.
 *
 * The starters are deliberately specific — "an event app" produces mush, "RSVP for a
 * birthday dinner" produces an app — so tapping one goes straight to a plan.
 */
export default function Idea() {
  const navigate = useNavigate();
  const setDescription = useDraft((s) => s.setDescription);
  const setAppPlan = useDraft((s) => s.setPlan);
  const stored = useDraft((s) => s.description);
  // saddle-sites.com's hero links here with the sentence the visitor already typed. It
  // only fills the field — nothing is planned or spent until they press Continue.
  const [params, setParams] = useSearchParams();
  const fromLink = params.get('idea')?.trim().slice(0, MAX_LINKED_IDEA);
  const [idea, setIdea] = useState(fromLink || stored);

  /*
   * Read once, then taken out of the address bar.
   *
   * The sentence is the person's own words — an address, a child's name — and while it sits
   * in the URL, every PageView the Meta pixel sends carries the full URL with it. The privacy
   * policy says Meta is never sent prompts. Only `idea` goes: `fbclid` and `utm_*` are the
   * ad attribution the pixel exists for, and they stay. The field already holds the text.
   * ConsentBanner also waits for this param to be gone, so neither side relies on the order
   * the two effects run in.
   */
  useEffect(() => {
    if (!params.has('idea')) return;
    const next = new URLSearchParams(params);
    next.delete('idea');
    setParams(next, { replace: true });
    // Once, on arrival: the link is read a single time and the field owns the text after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const go = (text: string): void => {
    const goal = text.trim();
    if (!goal) return;
    setDescription(goal);
    // Cleared so the plan screen always starts from a scaffold built for THIS idea,
    // never a plan left over from a previous run.
    setAppPlan(null);
    navigate('/new/plan');
  };

  return (
    <Screen title="What do you want to make?">
      <p className="type-body idea-sub">
        Describe it in a line. You&rsquo;ll see the plan before anything is built.
      </p>

      <label className="type-caption field-label" htmlFor="idea">
        Describe the app you want
      </label>
      <textarea
        id="idea"
        className="field idea-input"
        value={idea}
        rows={3}
        onChange={(e) => setIdea(e.target.value)}
        placeholder="An RSVP page for my sister's baby shower…"
      />
      <Button title="Continue" onPress={() => go(idea)} disabled={idea.trim().length === 0} />

      <h2 className="type-micro section-label">Or start from one of these</h2>
      <div className="starter-grid">
        {STARTER_PROMPTS.map((s) => (
          <button
            key={s.label}
            type="button"
            className="card clay-card pressable starter"
            aria-label={`${s.label}. ${s.prompt}`}
            onClick={() => go(s.prompt)}
          >
            <span className="type-heading">{s.label}</span>
            <span className="type-caption starter-sub">{s.prompt}</span>
          </button>
        ))}
      </div>
    </Screen>
  );
}
